'use client';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Fire a GA4 custom event.
 * Works in production only — silently no-ops in dev/test.
 */
export function trackEvent(eventName: string, params: Record<string, any> = {}) {
  if (typeof window === 'undefined') return;
  if (!window.gtag) return;
  window.gtag('event', eventName, params);
}

// ─── Platform-specific tracking helpers ──────────────────────────────────────

/** Called when a new customer account is created */
export function trackSignup(params: { method?: string; role?: string } = {}) {
  trackEvent('sign_up', {
    method: params.method ?? 'email',
    user_role: params.role ?? 'customer',
  });
}

/** Called when a table booking is fully confirmed (deposit paid) */
export function trackBookingComplete(params: {
  bookingRef: string;
  tavernName?: string;
  partySize?: number;
  depositAmount?: number;
  date?: string;
}) {
  trackEvent('booking_complete', {
    booking_ref: params.bookingRef,
    tavern_name: params.tavernName,
    party_size: params.partySize,
    deposit_amount: params.depositAmount,
    booking_date: params.date,
    currency: 'EUR',
  });
}

/** Called when the user lands on the payment step of the booking flow */
export function trackPaymentStepReached(params: {
  tavernName?: string;
  partySize?: number;
  depositAmount?: number;
}) {
  trackEvent('payment_step_reached', {
    tavern_name: params.tavernName,
    party_size: params.partySize,
    deposit_amount: params.depositAmount,
    currency: 'EUR',
  });
}

/** Called when a Stripe payment intent is successfully confirmed */
export function trackPaymentSuccess(params: {
  amount?: number;
  tavernName?: string;
  bookingRef?: string;
}) {
  trackEvent('purchase', {
    currency: 'EUR',
    value: params.amount,
    tavern_name: params.tavernName,
    booking_ref: params.bookingRef,
    transaction_id: params.bookingRef,
  });
}

/** Called when a payment fails or is cancelled */
export function trackPaymentFailure(params: { reason?: string; tavernName?: string } = {}) {
  trackEvent('payment_failure', {
    failure_reason: params.reason ?? 'unknown',
    tavern_name: params.tavernName,
  });
}

/** Called when a new event is successfully created */
export function trackEventCreation(params: {
  eventId?: string;
  eventName?: string;
  isSuperAdmin?: boolean;
}) {
  trackEvent('event_created', {
    event_id: params.eventId,
    event_name: params.eventName,
    is_super_admin: params.isSuperAdmin ?? false,
  });
}

/** Called when an event payment (Stripe Checkout) is completed */
export function trackEventPaymentComplete(params: {
  eventId?: string;
  eventName?: string;
  sessionId?: string;
}) {
  trackEvent('event_payment_complete', {
    event_id: params.eventId,
    event_name: params.eventName,
    session_id: params.sessionId,
    currency: 'EUR',
  });
}
